import { api } from '@/lib/api';
import { toast, dismissToast } from '@/components/ui/toast';

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

const PROVIDER_LABEL: Record<'eroad' | 'blackhawk', string> = {
  eroad: 'EROAD',
  blackhawk: 'Blackhawk',
};

interface SyncRunRecord {
  id?: string;
  _id?: string;
  status: 'running' | 'complete' | 'failed';
  counts?: { fetched: number; matched: number; updated: number; created: number; errors: number; notInFeed?: number };
  error?: string | null;
}

/** Whether an Assets-report row can take a targeted sync from `provider`.
 *  Mirrors the server's `findSyncableAssets`: Atlas-owned, linked to the
 *  provider, and Active (blank counts as Active). */
export function canSyncAssetFrom(provider: 'eroad' | 'blackhawk', row: any): boolean {
  const providerId = provider === 'eroad' ? row?.eroadId : row?.blackhawkId;
  return Boolean(providerId)
    && row?.assetType !== 'Contractor'
    && (!row?.vehicleStatus || row.vehicleStatus === 'Active');
}

/**
 * Kicks off a per-provider sync run on the server, then polls the run status
 * and surfaces progress / completion via toasts. Resolves once the run reaches
 * a terminal state (complete or failed) so the caller can refetch the table.
 *
 * With `assetIds`, only those assets are synced (the server drops any that are
 * not linked to the provider); without, the provider's whole fleet.
 */
export async function triggerAssetSync(
  provider: 'eroad' | 'blackhawk',
  navigate: (path: string) => void,
  assetIds?: string[],
): Promise<void> {
  const label = assetIds
    ? `${PROVIDER_LABEL[provider]} sync of ${assetIds.length} asset${assetIds.length === 1 ? '' : 's'}`
    : `${PROVIDER_LABEL[provider]} sync`;

  // 1. Create the run. A refusal (e.g. nothing in the selection is linked to
  // this provider) is shown as a toast rather than thrown, so the row and bulk
  // menus don't need their own error handling.
  const r = await api<{ id: string }>('/sync-runs', {
    method: 'POST',
    body: JSON.stringify(assetIds ? { provider, assetIds } : { provider }),
  });
  if (!r.success || !r.data?.id) {
    toast(`Could not start ${label}`, {
      description: r.error?.message || 'Please try again.',
      kind: 'error',
      durationMs: 8000,
    });
    return;
  }
  const runId = r.data.id;

  // 2. Sticky "in progress" toast — dismissed once the run finishes.
  const progressToastId = toast(`${label} started`, {
    description: 'Running in background. You can keep working.',
    durationMs: 0,
    action: {
      label: 'View',
      onClick: () => navigate(`/sync-runs/${runId}`),
    },
  });

  // 3. Poll until terminal state.
  const startedAt = Date.now();
  let finalRun: SyncRunRecord | null = null;
  while (Date.now() - startedAt < POLL_TIMEOUT_MS) {
    await new Promise((res) => setTimeout(res, POLL_INTERVAL_MS));
    const poll = await api<SyncRunRecord>(`/sync-runs/${runId}`);
    if (!poll.success || !poll.data) continue;
    if (poll.data.status === 'complete' || poll.data.status === 'failed') {
      finalRun = poll.data;
      break;
    }
  }

  dismissToast(progressToastId);

  if (!finalRun) {
    toast(`${label} still running`, {
      description: 'It is taking longer than expected — open the run to check progress.',
      kind: 'default',
      action: { label: 'View', onClick: () => navigate(`/sync-runs/${runId}`) },
    });
    return;
  }

  if (finalRun.status === 'failed') {
    toast(`${label} failed`, {
      description: finalRun.error ?? 'See the run for details.',
      kind: 'error',
      durationMs: 8000,
      action: { label: 'View details', onClick: () => navigate(`/sync-runs/${runId}`) },
    });
    return;
  }

  const c = finalRun.counts ?? { fetched: 0, matched: 0, updated: 0, created: 0, errors: 0 };
  toast(`${label} complete`, {
    description: assetIds
      ? `Updated ${c.updated} of ${c.matched} matched` +
        (c.notInFeed ? ` • ${c.notInFeed} not reported by ${PROVIDER_LABEL[provider]}` : '') +
        (c.errors ? ` • Errors ${c.errors}` : '')
      : `Fetched ${c.fetched} • Matched ${c.matched} • Updated ${c.updated} • New ${c.created}` +
        (c.errors ? ` • Errors ${c.errors}` : ''),
    kind: c.errors ? 'default' : 'success',
    durationMs: 8000,
    action: { label: 'View details', onClick: () => navigate(`/sync-runs/${runId}`) },
  });
}
